import { router, Link as InertiaLink } from "@inertiajs/react";
import AppLayout from "../../layouts/AppLayout";

interface DailyBucket {
  date: string;
  total_cost_micros: number;
  eval_count: number;
}

interface EvalRow {
  id: number;
  job_id: number;
  job_title: string | null;
  firm_name: string | null;
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_cost_micros: number;
  confidence: string | null;
  worth_sourcing: boolean;
  failure_reason: string | null;
  evaluated_at: string;
}

interface Props {
  mtd_micros: number;
  mtd_count: number;
  success_rate: number;
  daily: DailyBucket[];
  evals: EvalRow[];
  page: number;
  total_pages: number;
}

function dollars(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function DailySparkline({ daily }: { daily: DailyBucket[] }) {
  if (daily.length === 0) {
    return <p className="text-xs text-gray-400 italic">No evaluations this month yet.</p>;
  }
  const max = Math.max(...daily.map((d) => d.total_cost_micros), 1);
  const sorted = [...daily].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="flex items-end gap-1 h-16">
      {sorted.map((d) => {
        const pct = (d.total_cost_micros / max) * 100;
        return (
          <div key={d.date} className="flex flex-col items-center gap-0.5 flex-1 min-w-0" title={`${d.date}: ${dollars(d.total_cost_micros)} (${d.eval_count} evals)`}>
            <div
              className="w-full bg-purple-300 rounded-sm"
              style={{ height: `${Math.max(pct, 4)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function SettingsUsage({ mtd_micros, mtd_count, success_rate, daily, evals, page, total_pages }: Props) {
  return (
    <AppLayout title="Settings — AI Usage">
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <InertiaLink href="/settings" className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Settings</InertiaLink>
          <h1 className="text-xl font-semibold text-gray-900">AI Evaluation Usage</h1>
        </div>

        {/* Month-to-date stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { label: "MTD Spend", value: dollars(mtd_micros) },
            { label: "Evaluations", value: mtd_count.toLocaleString() },
            { label: "Success Rate", value: `${success_rate}%` },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{s.label}</p>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Daily sparkline */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Daily Spend — This Month</h2>
          </div>
          <div className="px-5 py-4">
            <DailySparkline daily={daily} />
          </div>
        </section>

        {/* Per-evaluation log */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Evaluation Log</h2>
          </div>
          {evals.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No evaluations recorded yet.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase">Job</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase">Tokens</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase">Result</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500 uppercase">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {evals.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 max-w-[180px] truncate">
                      <InertiaLink href={`/jobs/${e.job_id}`} className="text-gray-800 hover:text-green-700 font-medium truncate block">
                        {e.firm_name || "—"}
                      </InertiaLink>
                      {e.job_title && <span className="text-gray-400 truncate block">{e.job_title}</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500 tabular-nums">
                      {(e.input_tokens + e.output_tokens).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 tabular-nums font-medium">
                      {dollars(e.total_cost_micros)}
                    </td>
                    <td className="px-4 py-2">
                      {e.failure_reason ? (
                        <span className="text-red-500" title={e.failure_reason}>Error</span>
                      ) : e.worth_sourcing ? (
                        <span className="text-green-600">✓ Proceed</span>
                      ) : (
                        <span className="text-gray-400">✗ Skip</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-400 whitespace-nowrap">{fmt(e.evaluated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {total_pages > 1 && (
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400">Page {page} of {total_pages}</span>
              <div className="flex gap-2">
                {page > 1 && (
                  <button onClick={() => router.get("/settings/usage", { page: page - 1 })}
                    className="text-xs px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
                    ← Prev
                  </button>
                )}
                {page < total_pages && (
                  <button onClick={() => router.get("/settings/usage", { page: page + 1 })}
                    className="text-xs px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
                    Next →
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
